import clone from 'clone';
import { Subject } from 'rxjs';
import mustache from 'mustache';
import { Gizmo } from '../../gizmos';
import {
  IBinderSetDataParams,
  IGizmo,
  IGizmoCallbacks,
  IGizmoCatalogOptionV2,
  IGizmoCatalogV2,
  IGizmoData,
  IGizmoEvent,
  IRealmFormGizmo,
  IRootGizmoData,
  TGizmoData,
} from '../../types';
import { shuffleArray, simulateCallbacksForFilledForm } from './utils';
import { isListEqual, naturalSort, snov } from '../../utils';
import CommonBinder from '../common-binder';
import { getAnnexFormsIndex } from './annex-form-binder';
import { Flux } from '../../flux';

interface ISelectFieldBinder {
  init(): Promise<void>;
}

class SelectFieldBinder extends CommonBinder implements ISelectFieldBinder {
  /**
   * This variable will be set to true when the binder init method is called
   */
  private _initializing: boolean = false;
  /**
   * Options that will be shown in the UI
   */
  private _options: IGizmoCatalogOptionV2[];
  /**
   * Present when the user selects the other option
   */
  private _otherValue?: string;
  /**
   * This variable was introduced because of online catalogs, what happens is that the gizmo maybe
   * subscribed to listen to values of another gizmo and when this happens and the current gizmo
   * has a parent select_field gizmo the options won't be present yet, so we need to store the
   * value in here and when the options are ready we will set the value.
   */
  private _pendingValue?: string;
  private _pendingData?: IGizmoData;
  /**
   * Subject used by the UI layer to listen to options changes
   */
  public options: Subject<IGizmoCatalogOptionV2[]>;
  /**
   * Selected options list
   */
  public selectedOptions: Subject<IGizmoCatalogOptionV2[]>;
  /**
   * Observable which will be used by the UI layer
   */
  public otherValue: Subject<string>;

  constructor(gizmo: Gizmo) {
    super(gizmo);
    this._options = [];

    this.options = new Subject<IGizmoCatalogOptionV2[]>();
    this.selectedOptions = new Subject<IGizmoCatalogOptionV2[]>();
    this.otherValue = new Subject<string>();

    this._gizmo.data.subscribe((_: TGizmoData) => {
      this.selectedOptions.next(this.getSelected());
    });
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config: IGizmo = this._gizmo.getConfig();
      const rootGizmoConfig: IGizmo = this._gizmo.getCallbacks().getInfoCB({
        type: 'rootGizmoConfig',
      });
      this._initializing = true;

      if (rootGizmoConfig?.ops?.form?.onlineCatalogs === true) {
        this._initializing = false;
        await this._runFlux();

        this.options.subscribe((options: IGizmoCatalogOptionV2[]) => {
          if (
            this._pendingData &&
            this._pendingData.v &&
            typeof this._pendingData.v === 'string'
          ) {
            // If we have pending data when the options are ready we're going to set the value
            this.setValue(this._pendingData.v);
            this._pendingData = undefined;
          }

          if (
            this._pendingValue !== undefined &&
            typeof this._pendingValue === 'string' &&
            options.length > 0
          ) {
            this.setValue(this._pendingValue);
            this._pendingValue = undefined;
          }
        });
        // If we have online catalogs configured we're going to stop the initialization process
        // because we don't have the options yet
        return resolve();
      }

      this._options = await this._calculateOptions();
      this.options.next(this._options);

      const data = this._gizmo.getData();
      if (data !== undefined && !Array.isArray(data)) {
        if (data.sod?.f_id === 'o0') {
          await this.setValue('Otro');
          await this.setOtherValue(data.v.toString());
        } else {
          await this.setValue(data.v.toString());
        }
      } else if (data !== undefined && Array.isArray(data)) {
        const values: string[] = [];
        let other: string | undefined;

        for (const d of data) {
          if (d.sod?.f_id === 'o0') {
            other = d.v.toString();
            values.push('Otro');
          } else {
            values.push(d.v.toString());
          }
        }

        await this.setValue(values);

        if (other) {
          await this.setOtherValue(other);
        }
      }

      // If no data is assigned and we have a value configuration we'll evaluate the flux
      // script and look for an option that has that value
      // if (
      //   config.ops?.value?.flux &&
      //   !data
      //   // TODO: we're missing -> Object.keys(data).length === 0
      //   // TODO: with new Flowy data format maybe it's going to change
      // ) {
      //   const flux = this._gizmo.getFlux();

      //   if (flux) {
      //     let conditionResult = true;

      //     if (config.ops.value.condition) {
      //       const cr = await flux.evaluate({
      //         script: config.ops.value.condition,
      //       });

      //       if (cr !== undefined) {
      //         conditionResult = cr;
      //       }
      //     }

      //     if (conditionResult === true) {
      //       const result = await flux.evaluate({
      //         script: config.ops.value.flux,
      //       });

      //       if (result) {
      //         const optionToSelect = this._options.find(
      //           o => o.value === result
      //         );

      //         if (optionToSelect) {
      //           this.setSelected([optionToSelect]);
      //         }
      //       }
      //     }
      //   }
      // }
      this._runFlux();

      this._initializing = false;

      resolve();
    });
  };

  private _runFlux = async () => {
    const config: IGizmo = this._gizmo.getConfig();
    const flux = this._gizmo.getFlux();
    const data = this._gizmo.getData();

    if (flux && config.ops?.value?.flux && !data) {
      let conditionResult = true;

      if (config.ops.value.condition) {
        const cr = await flux.evaluate({
          script: config.ops.value.condition,
        });

        if (cr !== undefined) {
          conditionResult = cr;
        }
      }

      if (conditionResult === true) {
        const result = await flux.evaluate({
          script: config.ops.value.flux,
        });

        if (result) {
          await this.setValue(result);
        }
      }
    }
  };

  /**
   * Set the selected options, which will set the gizmo data
   * @param {IGizmoCatalogOptionV2[]} options list of options that will be set
   * @param {boolean} setOther when set to true it means that the selected option is being
   *  set by the setOtherValue method
   */
  public setSelected = (
    options: IGizmoCatalogOptionV2[],
    setOther?: boolean
  ): Promise<void> => {
    return new Promise<void>(async resolve => {
      const setDataWithOther = (option: IGizmoCatalogOptionV2) => {
        // When the option is the other option we're going to set the value to empty,
        // this is necessary so we can correctly validate the required flag. Because if
        // other option is being set but the value is empty we're going to show the error
        const data: IGizmoData = { v: option.value, sod: option };

        if (option.f_id === 'o0' && !setOther) {
          data.v = '';
        }

        return data;
      };

      if (options.length === 0) {
        // If no options are selected we're going to clear gizmo's data
        await this._gizmo.setData();

        return resolve();
      }

      const config: IGizmo = this._gizmo.getConfig();

      const selected = this.getSelected();
      if (selected.length > 0) {
        if (isListEqual(selected, options)) {
          // If we're trying ot assign the same options we're going to return, we're doing this
          // in order to stop sending unnecessary events
          return resolve();
        }
      }

      //? Should we change it to always be an array?
      if (!config.ops?.selectField?.isMultiple) {
        await this._gizmo.setData(setDataWithOther(options[0]));
      } else {
        const data: IGizmoData[] = options.map(o => ({ v: o.value, sod: o }));
        await this._gizmo.setData(data);
      }

      // We're going to clear the other value when the user selects an option
      this._otherValue = '';
      this.otherValue.next(this._otherValue);

      resolve();
    });
  };

  /**
   * Get the selected catalog options from the gizmo data
   *
   * @return {IGizmoCatalogOptionV2[]} list of selected options
   */
  public getSelected = (): IGizmoCatalogOptionV2[] => {
    const selectedOptions: IGizmoCatalogOptionV2[] = [];
    const data: IGizmoData | IGizmoData[] | undefined = this._gizmo.getData();

    if (data && !Array.isArray(data) && data.sod) {
      const sod = clone(data.sod);
      if (sod.f_id === 'o0') {
        sod.value = 'Otro';
      }
      selectedOptions.push(sod);
    } else if (data && Array.isArray(data)) {
      for (const d of data) {
        if (d.sod) {
          const sod = clone(d.sod);
          if (sod.f_id === 'o0') {
            sod.value = 'Otro';
          }

          selectedOptions.push(sod);
        }
      }
    }

    return selectedOptions;
  };

  /**
   * Set the other option value
   * @param value
   */
  public setOtherValue = (value: string): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      this._otherValue = value;
      this.otherValue.next(value);

      const options = clone(this.getSelected());

      for (const selectedOption of options) {
        if (selectedOption.f_id === 'o0') {
          selectedOption.value = value;
          break;
        }
      }

      let data: TGizmoData;
      if (!config.ops?.selectField?.isMultiple) {
        data = { v: options[0].value, sod: options[0] };
      } else {
        data = options.map(o => ({ v: o.value, sod: o }));
      }

      await this._gizmo.setData(data);

      resolve();
    });
  };

  /**
   * Get the value set by the user in the other optionß
   * @returns {string | undefined} the value of the other option
   */
  public getOtherValue = (): string | undefined => {
    return this._otherValue;
  };

  /**
   * Look through the options and find the one that has the value passed as parameter, or
   * if the value is an array it will look for all the options that have the values in the
   * value field
   *
   * @param {string | string[]} value value that will be searched in the options
   * @returns
   */
  public setValue = (value?: string | string[]): Promise<void> => {
    return new Promise<void>(async resolve => {
      console.log(this._gizmo.getConfig().label, ' ---- value: ', value);
      if (value === undefined) {
        // If value is undefined we're going to clear the selected options
        await this.setSelected([]);
        return resolve();
      }

      const valueSelected = this.getSelected().find(o => o.value === value);
      if (valueSelected && this._initializing === false) {
        return resolve();
      }

      if (Array.isArray(value)) {
        const options = this._options.filter(o =>
          value.includes(o.value.toString())
        );
        console.log('ops.1: ', options);
        if (options.length > 0) {
          await this.setSelected(options);
        }
      } else {
        const option = this._options.find(o => o.value === value);
        console.log('ops.2: ', option);
        if (option) {
          await this.setSelected([option]);
        } else {
          const rootGizmoConfig: IGizmo = this._gizmo.getCallbacks().getInfoCB({
            type: 'rootGizmoConfig',
          });

          // If no option was selected and the online catalogs are enabled we're going to
          // save the value in a variable to use it later. This will happen when an event is
          // setting the value but the online catalog still has not been loaded / updated.
          if (
            rootGizmoConfig?.ops?.form?.onlineCatalogs === true
            // &&
            // this._pendingValue === undefined
          ) {
            console.log(this._gizmo.getConfig().label, ' --------- pendingValue: ', value);
            this._pendingValue = value;
          }
        }
      }
      resolve();
    });
  };

  /**
   * Set data to the gizmo
   * @param data gizmo data to be set
   */
  public setData = async ({ data }: IBinderSetDataParams) => {
    if (this._options.length > 0) {
      if (data.v && typeof data.v === 'string') {
        await this.setValue(data.v);
      }
    } else {
      // If the options are still not ready we're going to save the data in a variable
      // to assign it later when the options are ready
      this._pendingData = data;
    }
  };

  // public setOtherValue = (value: string) => {}

  /**
   * Select field options
   * @returns {IGizmoCatalogOptionV2[]}
   */
  public getOptions = (): IGizmoCatalogOptionV2[] => {
    const config = this._gizmo.getConfig();
    const rootGizmoConfig: IGizmo = this._gizmo.getCallbacks().getInfoCB({
      type: 'rootGizmoConfig',
    });

    if (
      rootGizmoConfig?.ops?.form?.onlineCatalogs === true &&
      this._options.length === 0 &&
      !config.ops?.selectField?.parentFieldFid
    ) {
      this._gizmo.getCallbacks().getInfoCB({
        type: 'catalogOnline',
        catalogFid: config.ops?.selectField?.catalogFid,
        fid: config.fid,
      });
    }

    return this._options;
  };

  /**
   * Function to be called in order to calculate again the options, right now it's
   * being used in 'gizmos/gizmo-functions/handle-gizmo-event' when the gizmoData of
   * interest is being updated
   * @returns
   */
  public updateOptions = (eventType?: string): Promise<void> => {
    return new Promise<void>(async resolve => {
      const updatedOptions = await this._calculateOptions(eventType);
      this._options = updatedOptions;
      this.options.next(updatedOptions);

      resolve();
    });
  };

  /**
   * It calculate the options depending on the gizmo configuration
   * @returns {Promise<IGizmoCatalogOptionV2[]>}
   */
  private _calculateOptions = (
    eventType?: string
  ): Promise<IGizmoCatalogOptionV2[]> => {
    return new Promise<IGizmoCatalogOptionV2[]>(async resolve => {
      const rootGizmoConfig: IGizmo = this._gizmo.getCallbacks().getInfoCB({
        type: 'rootGizmoConfig',
      });
      const config: IGizmo = this._gizmo.getConfig();
      let options: IGizmoCatalogOptionV2[] = [];

      // This are the 'normal' options, the default catalog
      if (
        config &&
        config.ops &&
        config.ops.selectField &&
        !config.ops.selectField.parentFieldFid &&
        !config.ops.selectField.gizmoUid
      ) {
        const catalog: IGizmoCatalogV2 = this._gizmo.getCallbacks().getInfoCB({
          type: 'catalog',
          catalogFid: config.ops.selectField.catalogFid,
        });

        if (catalog) {
          options = catalog.options as IGizmoCatalogOptionV2[];

          const selected = this.getSelected();

          if (
            selected.length > 0 &&
            rootGizmoConfig?.ops?.form?.onlineCatalogs === true
          ) {
            // TODO: ADD FIND GIZMOS THAT HAVE THIS GIZMO AS A PARENT, IN CASE THERE IS/ARE ANY
            // TODO: GENERATE THE EVENT
            const event: IGizmoEvent = {
              type: 'gizmo.data',
              src: {
                type: 'gizmo',
                gizmoFid: config.fid,
              },
              data: {
                gizmoData: {
                  type: 'parent-init-update-catalog',
                },
              },
            };

            this._gizmo.getEvents().next(event);
          }
        }
      }

      // In here we're setting options from another Gizmo data, right now we have support
      // for 'annex_form' and 'data_table_field'
      if (config.ops?.selectField?.gizmoUid) {
        // We're retrieving the whole variable in case we're using some accessors
        const influxVar = config.ops.selectField.gizmoUid;
        const [uid] = config.ops.selectField.gizmoUid.split('.');
        const externalGizmo:
          | Gizmo
          | undefined = this._gizmo.getCallbacks().getInfoCB({
          type: 'gizmo',
          uid,
        });
        const rootGizmoFid = this._gizmo.getCallbacks().getInfoCB({
          type: 'rootGizmoFid',
        });
        const rootGizmoData = this._gizmo.getCallbacks().getInfoCB({
          type: 'rootGizmoData',
        });
        const fid = this._gizmo.getCallbacks().getInfoCB({
          type: 'uidToFid',
          uid,
        });

        if (rootGizmoFid && externalGizmo && rootGizmoData) {
          this._options = [];
          options = options.concat(
            await this._getExternalGizmoOptions({
              externalGizmo,
              fid,
              rootGizmoData,
              rootGizmoFid,
              gizmoUid: config.ops.selectField.gizmoUid,
              influxVar,
            })
          );
        }
      }

      if (config.ops?.selectField?.gizmoUids) {
        options = [];
        this._options = [];

        for (const gizmoUid of config.ops.selectField.gizmoUids) {
          const externalGizmo:
            | Gizmo
            | undefined = this._gizmo.getCallbacks().getInfoCB({
            type: 'gizmo',
            uid: gizmoUid,
          });
          const rootGizmoFid = this._gizmo.getCallbacks().getInfoCB({
            type: 'rootGizmoFid',
          });
          const rootGizmoData = this._gizmo.getCallbacks().getInfoCB({
            type: 'rootGizmoData',
          });
          const fid = this._gizmo.getCallbacks().getInfoCB({
            type: 'uidToFid',
            uid: gizmoUid,
          });

          if (rootGizmoFid && externalGizmo && rootGizmoData) {
            options = options.concat(
              await this._getExternalGizmoOptions({
                externalGizmo,
                fid,
                rootGizmoData,
                rootGizmoFid,
                gizmoUid,
              })
            );
          }
        }
      }

      // This will happend when the select field has a parent, previously known as
      // 'options_dependant'
      if (config.ops?.selectField?.parentFieldFid) {
        const { parentFieldFid } = config.ops.selectField;

        const rootGizmoData: IRootGizmoData = this._gizmo
          .getCallbacks()
          .getInfoCB({
            type: 'rootGizmoData',
          });

        if (rootGizmoData?.d && rootGizmoData.d[parentFieldFid]) {
          const parentGizmoData = rootGizmoData.d[parentFieldFid];

          if (!Array.isArray(parentGizmoData)) {
            const catalog: IGizmoCatalogV2 = this._gizmo
              .getCallbacks()
              .getInfoCB({
                type: 'catalog',
                catalogFid: config.ops.selectField.catalogFid,
              });
            const parentOptions: {
              [fid: string]: IGizmoCatalogOptionV2[];
            } = catalog.options as any;

            if (parentGizmoData.sod?.f_id) {
              // If no options are found we set it to an empty array
              let id =
                parentGizmoData.sod?._id || parentGizmoData.sod?.id || '';
              options = parentOptions[id] || [];
              this._options = options;

              if (
                this._options.length > 0 &&
                this._pendingValue &&
                eventType === 'update-catalog'
              ) {
                await this.setValue(this._pendingValue);

                this._pendingValue = undefined;
              }

              if (
                this._options.length === 0 &&
                eventType !== 'update-catalog' &&
                rootGizmoConfig?.ops?.form?.onlineCatalogs === true
              ) {
                this._gizmo.getCallbacks().getInfoCB({
                  type: 'catalogOnline',
                  catalogFid: config.ops.selectField.catalogFid,
                  fid: config.fid,
                  parentCatalogFid: config.ops.selectField.parentFieldFid,
                  data: id,
                });
              }

              let resetSelected = true;
              const selectedOptions = this.getSelected();
              // We're adding this if to handle when the user has selected the same parent option,
              // if this has happened the selected option will be present on the new options. If
              // that's the case we won't reset the selectedOptions.
              if (selectedOptions.length > 0) {
                const selectedOption = options.find(
                  so => so.f_id === selectedOptions[0].f_id
                );

                if (selectedOption) {
                  resetSelected = false;

                  if (
                    eventType === 'update-catalog' &&
                    rootGizmoConfig?.ops?.form?.onlineCatalogs === true
                  ) {
                    // this.setSelected([selectedOption]);
                    const event: IGizmoEvent = {
                      type: 'gizmo.data',
                      src: {
                        type: 'gizmo',
                        gizmoFid: config.fid,
                      },
                      data: {
                        gizmoData: {
                          type: 'parent-init-update-catalog',
                        },
                      },
                    };

                    this._gizmo.getEvents().next(event);
                  }
                }
              }

              if (
                eventType !== undefined &&
                rootGizmoConfig?.ops?.form?.onlineCatalogs === true
              ) {
                resetSelected = false;
              }

              if (resetSelected) {
                // When we're setting the new options derived of the parent selection
                // we're going to clear the selected options.
                await this.setSelected([]);
              }

              if (options.length === 1 && options[0].value === 'NA') {
                await this.setSelected(options);
              }
            }
          }
        } else {
          this._options = [];
          // If the parent has no data we're going to clear the selected options
          await this.setSelected([]);
        }
      }

      let sort = 'none';
      if (config.ops?.selectField?.sort) {
        sort = config.ops.selectField.sort;
      }

      if (sort !== 'none') {
        options = options.sort((a, b) => {
          const textA = a.value.toString().toUpperCase();
          const textB = b.value.toString().toUpperCase();

          if (sort === 'asc') {
            return naturalSort(textA, textB);
          } else {
            return -naturalSort(textA, textB);
          }
        });
      }

      if (options.length > 0 && config.ops?.selectField?.randomOrder) {
        options = shuffleArray(options);
      }

      if (options.length > 0 && config.ops?.selectField?.other === true) {
        options.push({
          f_id: 'o0',
          value: 'Otro',
        });
      }

      resolve(options);
    });
  };

  private _getExternalGizmoOptions = async ({
    externalGizmo,
    fid,
    rootGizmoData,
    rootGizmoFid,
    gizmoUid,
    influxVar,
  }: IGetExternalGizmoOptionsParams): Promise<IGizmoCatalogOptionV2[]> => {
    const options: IGizmoCatalogOptionV2[] = [];
    const externalConfig = externalGizmo.getConfig();
    const dao = externalGizmo.getDao();
    const config = this._gizmo.getConfig();

    if (externalConfig.type === 'annex_form' && rootGizmoData.d && dao) {
      try {
        const data = rootGizmoData.d[fid];

        if (
          data &&
          Array.isArray(data) &&
          externalConfig.ops?.annexForm?.rootGizmoFid
        ) {
          const realmFormGizmo: IRealmFormGizmo = await dao.getDatabase()?.get({
            type: 'forms',
            fid: externalConfig.ops?.annexForm?.rootGizmoFid,
          });
          const form = realmFormGizmo?.formGizmo;

          const index = await getAnnexFormsIndex({
            data,
            includeRegisters: true,
            context: {
              gizmo: externalGizmo,
            },
          });

          if (form && index?.registers) {
            for (const register of index.registers) {
              let loadOptions = true;
              // Value that will be shown on the option field
              let value: any = '';
              const callbacks = simulateCallbacksForFilledForm({
                form,
                filledForm: register,
              });

              if (config.ops?.selectField?.filter) {
                const flux = new Flux({
                  gizmoCallbacks: callbacks,
                });
                await flux.init();

                const result = await flux.evaluate({
                  script: config.ops.selectField.filter,
                });

                if (typeof result === 'boolean') {
                  loadOptions = result;
                }
              }

              if (!loadOptions) {
                // If the option is not going to be loaded we continue to the next
                // loop iteration
                continue;
              }

              if (config.ops?.selectField?.value) {
                const mustacheData = {};
                const matches = config.ops.selectField.value.match(
                  /{{\s*[\w\.]+\s*}}/g
                );

                if (matches) {
                  for (const match of matches) {
                    const fluxVar = match
                      .replace('{{', '')
                      .replace('}}', '')
                      .trim();
                    const v = callbacks.getInfoCB({
                      type: 'data',
                      uid: fluxVar,
                    });

                    if (v && v.v) {
                      snov(mustacheData, fluxVar, v.v);
                    }
                  }

                  const result = mustache.render(
                    config.ops.selectField.value
                      .replace(/{{/g, '{{{')
                      .replace(/}}/g, '}}}'),
                    mustacheData
                  );
                  value = result;
                }
              } else if (register.d && Object.keys(register.d).length > 0) {
                const firstAnswer = register.d[Object.keys(register.d)[0]];
                if (firstAnswer && !Array.isArray(firstAnswer)) {
                  value = firstAnswer.v;
                }
              }

              const option: IGizmoCatalogOptionV2 = {
                _id: register.fid,
                formId: register.formId,
                // catalogFid: externalConfig.ops?.annexForm?.rootGizmoFid,
                // TODO: check how this catalog fid should be set, on v2 when had it like this,
                // setting it with the register fid. But I have my doubts that this is correct.
                catalogFid: register.fid,
                f_id: register.fid,
                value,
              };

              options.push(option);
            }
          }
        }
      } catch (e) {}
    } else if (externalConfig.type === 'select_field' && rootGizmoData.d) {
      let accesor: string | undefined;
      if (influxVar) {
        const splitted = influxVar.split('.');
        if (splitted.length > 1) {
          accesor = splitted[1];
        }
      }

      const data = rootGizmoData.d[fid];

      if (
        data &&
        !Array.isArray(data) &&
        data.sod &&
        data.v === 'NA' &&
        accesor === 'not_na'
      ) {
        if (externalConfig.ops?.selectField?.parentFieldFid) {
          const parentDataNotNA = this._getParentDataNotNA({
            parentGizmoFid: externalConfig.ops.selectField.parentFieldFid,
            gizmoCallbacks: this._gizmo.getCallbacks(),
          });

          if (parentDataNotNA?.sod) {
            options.push(parentDataNotNA.sod);
          }
        }
      } else if (data && !Array.isArray(data) && data.sod) {
        options.push(data.sod);
      } else if (data && Array.isArray(data)) {
        for (const d of data) {
          if (d.sod) {
            options.push(d.sod);
          }
        }
      }
    }

    return options;
  };

  private _getParentDataNotNA = ({
    parentGizmoFid,
    gizmoCallbacks,
  }: IGetParentDataNotNAParams): any => {
    const parentGizmo: Gizmo = gizmoCallbacks.getInfoCB({
      type: 'gizmo',
      fid: parentGizmoFid,
    });

    if (parentGizmo) {
      const data = parentGizmo.getData();

      if (data && !Array.isArray(data) && data.v) {
        const value = data.v;

        if (value === 'NA') {
          const config = parentGizmo.getConfig();

          if (config.ops?.selectField?.parentFieldFid) {
            return this._getParentDataNotNA({
              parentGizmoFid: config.ops.selectField.parentFieldFid,
              gizmoCallbacks,
            });
          } else {
            return data;
          }
        } else {
          return data;
        }
      }
    }
  };
}

interface IGetExternalGizmoOptionsParams {
  externalGizmo: Gizmo;
  fid: string;
  rootGizmoFid: string;
  rootGizmoData: IRootGizmoData;
  gizmoUid?: string;
  influxVar?: string;
}

interface IGetParentDataNotNAParams {
  parentGizmoFid: string;
  gizmoCallbacks: IGizmoCallbacks;
}

export default SelectFieldBinder;
